import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { useUserRole } from '../hooks/useUserRole.js';
import config from '../config.js';
import FollowersList from './partial/FollowersList.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import { useT } from '../contexts/LocaleContext.jsx';
import { richMessage } from '../_helpers/richMessage.jsx';
import '../styles/BandFollowersPage.css';

const PER_PAGE = 20;

const sortFollowers = (arr, field, dir) =>
  [...arr].sort((a, b) => {
    let aVal, bVal;
    if (field === 'display_name') {
      aVal = (a.display_name || '').toLowerCase();
      bVal = (b.display_name || '').toLowerCase();
    } else {
      const aMs = new Date(a.follow_date).getTime();
      const bMs = new Date(b.follow_date).getTime();
      aVal = isNaN(aMs) ? -Infinity : aMs;
      bVal = isNaN(bMs) ? -Infinity : bMs;
    }
    if (dir === 'desc') return bVal < aVal ? -1 : bVal > aVal ? 1 : 0;
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
  });

const BandFollowersPage = () => {
  const { bandid } = useParams();
  const navigate = useNavigate();
  const fetchWrapper = useFetchWrapper();
  const t = useT();
  // Read inside the fetch callback through a ref so it stays stable across locale switches.
  const tRef = useRef(t);
  tRef.current = t;
  // Members only (ENG-501): GET /bands/{id}/followers and promoting a
  // follower to moderator are both member-only on the backend, so moderators
  // get the Access Denied state like everyone else.
  const { isMember, isLoading: roleLoading } = useUserRole(bandid);

  const [allFollowers, setAllFollowers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Catalog key of the load error, translated at render so a locale switch
  // applies without refetching.
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [sortBy, setSortBy] = useState('follow_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchFollowers = useCallback(async () => {
    if (!bandid) return;
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchWrapper.get(`${config.apiBaseUrl}/bands/${bandid}/followers`);

      if (Array.isArray(data)) {
        setAllFollowers(
          data.map((f) => ({
            user_id: f.UserID,
            follow_date: f.FollowedAt,
            display_name: f.Name || f.Username || tRef.current('followers.page.userFallback', { id: f.UserID }),
            username: f.Username || `user${f.UserID}`,
            profile_image_url: f.ProfileImageURL,
          }))
        );
      } else {
        setAllFollowers([]);
      }
    } catch {
      setError('followers.page.loadError');
      setAllFollowers([]);
    } finally {
      setIsLoading(false);
    }
  }, [bandid, fetchWrapper]);

  useEffect(() => {
    if (isMember && !roleLoading) {
      fetchFollowers();
    }
  }, [fetchFollowers, isMember, roleLoading]);

  const filtered = useMemo(() => {
    let result = allFollowers;
    if (activeSearch) {
      const lower = activeSearch.toLowerCase();
      result = allFollowers.filter(
        (f) =>
          f.display_name.toLowerCase().includes(lower) ||
          f.username.toLowerCase().includes(lower)
      );
    }
    return sortFollowers(result, sortBy, sortDirection);
  }, [allFollowers, activeSearch, sortBy, sortDirection]);

  const totalCount = filtered.length;
  const totalPages = Math.ceil(totalCount / PER_PAGE);
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const pageFollowers = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    const [field, dir] = e.target.value.split('-');
    setSortBy(field);
    setSortDirection(dir);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (roleLoading) {
    return (
      <div className="bfp-loading">
        <LoadingSpinner />
        <p>{t('common.status.loadingEllipsis')}</p>
      </div>
    );
  }

  if (!isMember) {
    return (
      <div className="bfp-access-denied">
        <FaUsers className="bfp-access-denied__icon" />
        <h2 className="bfp-access-denied__title">{t('common.errors.accessDenied')}</h2>
        <p className="bfp-access-denied__text">{t('followers.page.accessDeniedText')}</p>
        <button className="bfp-btn bfp-btn--primary" onClick={() => navigate(-1)}>
          {t('common.actions.goBack')}
        </button>
      </div>
    );
  }

  const displayCount = activeSearch ? totalCount : allFollowers.length;

  return (
    <div className="bfp">
      <div className="bfp__title-row">
        <h2 className="bfp__title">
          {richMessage(t('followers.page.heading'), { count: <span className="bfp__count">{displayCount}</span> })}
        </h2>
      </div>

      <p className="bfp__subtitle">
        {t('followers.page.subtitle')}
      </p>

      <form className="bfp__controls" onSubmit={handleSearch}>
        <input
          type="text"
          className="bfp__search-input"
          placeholder={t('followers.page.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            if (e.target.value === '') {
              setActiveSearch('');
              setCurrentPage(1);
            }
          }}
        />
        <button type="submit" className="bfp-btn bfp-btn--primary">
          {t('common.actions.search')}
        </button>
        <label htmlFor="bfp-sort" className="bfp__sort-label">{t('followers.page.sortLabel')}</label>
        <select
          id="bfp-sort"
          className="bfp__sort-select"
          value={`${sortBy}-${sortDirection}`}
          onChange={handleSortChange}
        >
          <option value="follow_date-desc">{t('followers.page.sort.newest')}</option>
          <option value="follow_date-asc">{t('followers.page.sort.oldest')}</option>
          <option value="display_name-asc">{t('followers.page.sort.nameAscending')}</option>
          <option value="display_name-desc">{t('followers.page.sort.nameDescending')}</option>
        </select>
      </form>

      {error && (
        <div className="bfp__error" role="alert">
          <span>{t(error)}</span>
          <button className="bfp__error-retry" onClick={fetchFollowers}>{t('common.actions.retry')}</button>
        </div>
      )}

      <FollowersList
        followers={pageFollowers}
        isLoading={isLoading}
        currentPage={safePage}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={PER_PAGE}
        onPageChange={handlePageChange}
        onPromotionSuccess={fetchFollowers}
        bandId={bandid}
        hasActiveSearch={!!activeSearch}
      />
    </div>
  );
};

export default BandFollowersPage;
